/* =============================================================================
 * Taking the boot screen down
 * -----------------------------------------------------------------------------
 * index.html paints a logo before anything else exists, outside #root so that
 * React's first render cannot cut it off. Something has to remove it, and the
 * moment it is removed is the whole point.
 *
 * -----------------------------------------------------------------------------
 * TWO ATTEMPTS AT "WHEN", BOTH WRONG, AND WHY
 * -----------------------------------------------------------------------------
 * At React mount. The overlay came down as soon as the app existed — while the
 * session was still resolving — so the app put up its own loading screen and the
 * same animation restarted from nothing. A reload showed a still logo, then the
 * logo assembling, then the page.
 *
 * When the session resolved. Better, and still one layer short: with a signed-in
 * user the two-factor gate then runs its own check, and its loading state
 * appeared exactly as the overlay faded. Same fault, one component deeper.
 *
 * -----------------------------------------------------------------------------
 * WHAT IT DOES NOW
 * -----------------------------------------------------------------------------
 * The overlay is the application's only startup loading screen, and it stays up
 * until nothing is still deciding what to show. Two conditions, both required:
 *
 *   the session has resolved          bootScreenReady, from SessionProvider
 *   nothing is holding it             holdBootScreen, from FullScreenLoader
 *
 * Any screen that would otherwise draw its own full-page loader instead takes a
 * hold and draws nothing: the overlay is already there, already animating, and
 * already in the right place.
 * ========================================================================== */

/** The fade in index.html. Kept in step by a test rather than by memory. */
const FADE_MS = 320;

/**
 * Long enough for a loader that is about to mount to register its hold, short
 * enough that nobody sees a pause.
 *
 * React runs child effects before parent effects, so a gate's loader normally
 * takes its hold before SessionProvider reports the session resolved — but that
 * is an ordering guarantee this file should not have to depend on. A tick makes
 * it true regardless of which way round they run.
 */
const SETTLE_MS = 48;

let taken = false;
let sessionReady = false;
let holds = 0;
let scheduled = false;

/** The session has resolved: there is now a real screen behind the overlay. */
export function bootScreenReady(): void {
  sessionReady = true;
  consider();
}

/**
 * Keep the overlay up while this caller is still working.
 *
 * Returns the release. Call it on unmount — a hold that is never released is a
 * logo animating over an application nobody can reach, which is what the
 * failsafe in index.html exists to catch and not something to rely on.
 */
export function holdBootScreen(): () => void {
  if (taken) return () => {};

  holds += 1;

  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    consider();
  };
}

/**
 * Whether the overlay is still covering the page.
 *
 * For the screens that would otherwise draw a loading state nobody can see. The
 * app mounts while the overlay is still up, so without this there are two copies
 * of the mark animating at once — one on top of the other, the lower one behind
 * an opaque overlay. Invisible, and still a second animated SVG composited every
 * frame on a handset with neither the battery nor the GPU to spare.
 */
export function bootScreenIsUp(): boolean {
  if (taken) return false;
  if (typeof document === 'undefined') return false;
  return document.getElementById('boot') !== null;
}

function consider(): void {
  if (taken || scheduled) return;
  if (typeof window === 'undefined') return;

  scheduled = true;

  /* setTimeout rather than requestAnimationFrame: a tab that boots in the
     background never gets a frame, and the overlay would sit there until it was
     looked at. */
  window.setTimeout(() => {
    scheduled = false;
    if (!sessionReady || holds > 0) return;
    dismiss();
  }, SETTLE_MS);
}

function dismiss(): void {
  if (taken) return;
  taken = true;

  const boot = document.getElementById('boot');
  if (!boot) return;

  boot.classList.add('boot-done');

  /* Removed rather than left at opacity 0. It carries a running animation, and
     an animation on a hidden element is still work the compositor does on every
     frame for as long as the tab is open. */
  window.setTimeout(() => boot.remove(), FADE_MS + 60);
}
